import React, { useMemo } from 'react';
import { stripAccents, expandQueryTokens } from '../lib/fuzzySearch';

/**
 * Highlight — envuelve `text` y resalta cualquier coincidencia con los tokens
 * de `query` (post-normalización + expansión de alias).
 *
 * - Insensible a acentos y mayúsculas.
 * - Levenshtein no aplica aquí: solo subcadena del token canónico
 *   (suficiente porque el filtro ya hace el match difuso).
 * - Si no hay query o no hay match, devuelve el texto plano.
 */
export default function Highlight({ text, query, className = '' }) {
  const segments = useMemo(() => buildSegments(text || '', query || ''), [text, query]);
  if (segments.length === 1 && !segments[0].hit) return <>{text}</>;
  return (
    <>
      {segments.map((seg, i) =>
        seg.hit ? (
          <mark
            key={i}
            className={`bg-amber-200 text-amber-900 rounded px-0.5 ${className}`}
            data-testid="directory-card-highlight"
          >
            {seg.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        ),
      )}
    </>
  );
}

function buildSegments(text, query) {
  const tokens = expandQueryTokens(query);
  if (!tokens.length || !text) return [{ text, hit: false }];

  // Trabajamos sobre una versión sin acentos del texto para localizar matches,
  // pero devolvemos los fragmentos del texto ORIGINAL (con acentos y case
  // preservados) — sólo necesitamos saber dónde empieza/termina cada hit.
  const normalized = stripAccents(text);
  // Cada coincidencia es un par [start, end] en el espacio del texto original.
  // Como stripAccents preserva la longitud (NFD descompone pero el replace
  // remueve los marks → mismo número de code points en la mayoría de los
  // alfabetos latinos), los índices son alineados 1:1 con el texto original.
  const hits = [];
  for (const tok of tokens) {
    if (!tok) continue;
    let from = 0;
    while (from <= normalized.length - tok.length) {
      const idx = normalized.indexOf(tok, from);
      if (idx === -1) break;
      hits.push([idx, idx + tok.length]);
      from = idx + tok.length;
    }
  }
  if (!hits.length) return [{ text, hit: false }];

  // Merge overlapping hits.
  hits.sort((a, b) => a[0] - b[0]);
  const merged = [hits[0]];
  for (let i = 1; i < hits.length; i++) {
    const last = merged[merged.length - 1];
    if (hits[i][0] <= last[1]) last[1] = Math.max(last[1], hits[i][1]);
    else merged.push(hits[i]);
  }

  const out = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) out.push({ text: text.slice(cursor, s), hit: false });
    out.push({ text: text.slice(s, e), hit: true });
    cursor = e;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), hit: false });
  return out;
}
